import React from "react";
import { Modal } from "@hagerty/react-components";
import { phoneNumbers } from "shared";
import styles from "./PaperlessTermsModal.module.scss";
import { Link } from "../../../../../Link";

type PaperlessTermsModalProps = {
  id: string;
};

export const PaperlessTermsModal = ({ id }: PaperlessTermsModalProps) => {
  return (
    <Modal
      id={id}
      size="Default"
      header={"Terms and conditions"}
      className={styles["paperless-terms-modal"]}
    >
      <h2 className={styles.title}>Paperless Terms and Conditions</h2>
      <p>
        You may choose to receive your insurance policy documents electronically
        ("Paperless option"). Electronic insurance policy documents have exactly
        the same contractual force as paper documents. The Paperless option is
        voluntary and offered to you conditioned on your acceptance without
        modification of the terms, conditions, and notices contained herein
        ("Terms and Conditions"). Please read the following Terms and Conditions
        carefully. If you do not agree with any of these Terms and Conditions,
        do not enroll in the Paperless option.
      </p>
      <ModalHeader>Modification and Acceptance of Terms</ModalHeader>
      <p>
        Hagerty reserves the right to change these Terms and Conditions at any
        time. Your continued participation in the Paperless option will
        constitute your acceptance of any changes made to the Terms and
        Conditions. Please check the Terms and Conditions located on Policy
        Management at Hagerty.com.
      </p>
      <ModalHeader>No Unlawful or Prohibited Use</ModalHeader>
      <p>
        As a condition of your use of the Paperless option, you declare that you
        will not use the Web site for any purpose that is unlawful or prohibited
        by these Terms and Conditions, including but not limited to altering any
        insurance policy document made available to you electronically. You may
        not use the Hagerty website in any manner which could damage, disable,
        overburden, or impair the Hagerty website or interfere with any other
        party's use and enjoyment of the site. You may not obtain or attempt to
        obtain material or information through any means not intentionally made
        available to you or provided for through the Hagerty website.
      </p>
      <ModalHeader>
        What will I need to enroll in the Paperless option?
      </ModalHeader>
      <p>
        Before you enroll in the Paperless option, you must have regular access
        to a computer capable of connecting to the Internet, and a browser
        capable of viewing Hagerty's website. Your browser must be capable of
        downloading, displaying, printing, and retaining documents in the Adobe
        Acrobat Portable Display Format ("PDF"). You will also need a valid
        email account. In the event that we make changes to the requirements
        above such that you may no longer be capable of accessing your
        electronic insurance policy documents, we will ask you to review the new
        requirements and indicate your consent to continue participating in the
        Paperless option by following directions provided from Hagerty.
      </p>
      <ModalHeader>What are my responsibilities?</ModalHeader>
      <p>
        Confirm your email address and review the Terms and Conditions:
        <br />
        When you enroll in the Paperless option, we will ask you to verify your
        email address. You will also be required to review and consent to these
        Terms and Conditions. If you do not complete enrollment in the Paperless
        option, you will continue to receive paper versions of all insurance
        policy documents by United States Postal Service.
      </p>
      <ModalHeader>
        Inform Hagerty of changes to your email address:
      </ModalHeader>
      <p>
        When you enroll in the Paperless option, it is your responsibility to
        inform Hagerty of any subsequent changes to your email address and to
        keep your email account active and accessible. Hagerty is not
        responsible for problems arising from your email account. You can report
        a change or correction to your email address by calling Hagerty at{" "}
        <Link phoneNumber={phoneNumbers.auto} /> or by logging into your account
        and following the link to make changes to your client information.
      </p>
      <ModalHeader>Undeliverable email:</ModalHeader>
      <p>
        At any time, if it becomes clear to us that the email is undeliverable,
        we will un-enroll you in the Paperless option. You may re-enroll in the
        Paperless option by completing the enrollment process again and
        providing a valid email address. If you are un-enrolled in the Paperless
        option, you will receive paper versions of all insurance policy
        documents by United States Postal Service.
      </p>
      <ModalHeader>Which documents will I receive electronically?</ModalHeader>
      <p>
        When you enroll in the Paperless option, you will receive your new
        policy, renewal policy, and any endorsement documents electronically.
        This includes your policy ID cards. Please be aware that there may be
        some documents that we cannot deliver electronically due to legal and/or
        technology restrictions in your state. You will receive paper versions
        of these documents by United States Postal Service.
      </p>
      <ModalHeader>How can I access my documents?</ModalHeader>
      <p>
        When new documents are available to review, you will receive an email
        message with a link to Hagerty's website. It is then your responsibility
        to log in to the website to view, save or print your documents. You may
        also log in to the website at any time to review, save or print your
        documents.
        <br />
        You are responsible for maintaining the confidentiality of passwords
        associated with your Hagerty account. Please do not share your password.
        If you become aware of any unauthorized use of your password or of your
        account, please contact Hagerty immediately.
      </p>
      <ModalHeader>How do I un-enroll?</ModalHeader>
      <p>
        You may un-enroll at any time by calling Hagerty at{" "}
        <Link phoneNumber={phoneNumbers.auto} /> or by logging on to our website
        and following the instructions for un-enrolling from the Paperless
        option.
      </p>
      <ModalHeader>How can I obtain paper copies?</ModalHeader>
      <p>
        You may also request a paper copy of any insurance policy document at
        any time without un-enrolling in the Paperless option by contacting
        Hagerty. The paper copy of this document(s) will then be mailed to your
        postal address. You may be charged a fee for your request for paper
        copies, unless prohibited by law.
      </p>
      <ModalHeader>Please keep the following in mind.</ModalHeader>
      <p>
        Although there is no charge for the Paperless option, there may be costs
        associated with electronic access to the documents, such as usage
        charges from Internet access providers and telephone companies. These
        costs are your responsibility
      </p>
    </Modal>
  );
};

type ModalHeaderProps = {
  children: React.ReactNode;
};

const ModalHeader = ({ children }: ModalHeaderProps) => {
  return <h2 className="modal__section-title">{children}</h2>;
};
